import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { Badge, Spinner } from "../components/UI";
import dayjs from "dayjs";
import "dayjs/locale/de";
dayjs.locale("de");

const WOCHENTAGE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function Kalender() {
  const [sitzungen, setSitzungen] = useState(null);
  const [monat, setMonat] = useState(dayjs().startOf("month"));
  const [ausgewaehlterTag, setAusgewaehlterTag] = useState(null);

  useEffect(() => { api.getSitzungen().then(setSitzungen); }, []);

  const terminePerTag = useMemo(() => {
    const map = {};
    (sitzungen || []).forEach(s => {
      if (!s.datum) return;
      const key = dayjs(s.datum).format("YYYY-MM-DD");
      (map[key] = map[key] || []).push(s);
    });
    return map;
  }, [sitzungen]);

  if (!sitzungen) return <Spinner />;

  // Kalenderraster: Montag der ersten Woche bis Sonntag der letzten Woche
  // (Locale "de" -> Wochenstart Montag), damit das Gitter immer
  // vollständige Wochenreihen zeigt.
  const monatsStart = monat.startOf("month");
  const monatsEnde = monat.endOf("month");
  const gitterStart = monatsStart.startOf("week");
  const gitterEnde = monatsEnde.endOf("week");

  const tage = [];
  let cursor = gitterStart;
  while (cursor.isBefore(gitterEnde) || cursor.isSame(gitterEnde, "day")) {
    tage.push(cursor);
    cursor = cursor.add(1, "day");
  }

  const heute = dayjs();
  const termineAmAusgewaehltenTag = ausgewaehlterTag ? (terminePerTag[ausgewaehlterTag.format("YYYY-MM-DD")] || []) : [];

  return (
    <div>
      <div className="page-header">
        <div><div className="breadcrumb">Sitzungen</div><h2>Kalender</h2></div>
        <div className="page-actions">
          <a href="/api/kalender.ics" className="btn btn-secondary btn-sm" download>Als ICS abonnieren</a>
        </div>
      </div>

      <div className="content">
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <button className="btn btn-ghost btn-sm" onClick={() => setMonat(m => m.subtract(1, "month"))}>‹</button>
            <h3 style={{ margin: "0 12px", minWidth: 160, textAlign: "center" }}>{monat.format("MMMM YYYY")}</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setMonat(m => m.add(1, "month"))}>›</button>
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: "auto" }} onClick={() => setMonat(dayjs().startOf("month"))}>Heute</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid var(--border)" }}>
            {WOCHENTAGE.map(w => (
              <div key={w} style={{ padding: "8px 6px", fontSize: 10.5, fontWeight: 600, color: "var(--ink-soft)", textAlign: "center", textTransform: "uppercase", letterSpacing: 0.4 }}>
                {w}
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {tage.map(tag => {
              const key = tag.format("YYYY-MM-DD");
              const termine = terminePerTag[key] || [];
              const imMonat = tag.month() === monat.month();
              const istHeute = tag.isSame(heute, "day");
              const istAusgewaehlt = ausgewaehlterTag && tag.isSame(ausgewaehlterTag, "day");
              return (
                <div key={key}
                  onClick={() => termine.length && setAusgewaehlterTag(tag)}
                  style={{
                    minHeight: 74, padding: "6px 6px 4px", borderRight: "1px solid var(--border)",
                    borderBottom: "1px solid var(--border)", cursor: termine.length ? "pointer" : "default",
                    background: istAusgewaehlt ? "var(--primary-light)" : "var(--white)",
                    opacity: imMonat ? 1 : 0.4,
                  }}>
                  <div style={{
                    fontSize: 11.5, fontWeight: istHeute ? 700 : 400,
                    color: istHeute ? "var(--primary)" : "var(--ink)",
                    width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: "50%", background: istHeute ? "var(--primary-light)" : "transparent",
                  }}>
                    {tag.date()}
                  </div>
                  {termine.slice(0, 2).map(s => (
                    <div key={s.id} title={s.titel} style={{
                      fontSize: 10, marginTop: 3, padding: "1px 5px", borderRadius: 3,
                      background: "var(--info-bg)", color: "var(--info)",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {s.titel}
                    </div>
                  ))}
                  {termine.length > 2 && (
                    <div style={{ fontSize: 9.5, color: "var(--ink-faint)", marginTop: 2 }}>+{termine.length - 2} weitere</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {ausgewaehlterTag && (
          <div className="card">
            <div className="card-header"><h3>{ausgewaehlterTag.format("dddd, D. MMMM YYYY")}</h3></div>
            <table className="table">
              <tbody>
                {termineAmAusgewaehltenTag.map(s => (
                  <tr key={s.id}>
                    <td style={{ width: 70, fontFamily: "var(--mono)", fontSize: 12 }}>{dayjs(s.datum).format("HH:mm")}</td>
                    <td><Link to={`/sitzungen/${s.id}`}>{s.titel}</Link></td>
                    <td style={{ textAlign: "right" }}><Badge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
